import React from "react";
import "./MainPage.scss";
import Contact from "../Contact/Contact";
import { GUIDES } from "../Guides/GuideLayout";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import plateShield from "../../../img/home-tabliczka-tarcza.webp";
import plateGold from "../../../img/home-tabliczka-zlota.webp";
import heatPress from "../../../img/home-prasa-termiczna.webp";
import HowItWorks from "./HowItWorks";

// Strona główna w stylu ulotki: granatowy nagłówek, szeryfowe tytuły, jasne karty z cienką ramką.

// Poradniki wyróżnione na stronie głównej (w tej kolejności) - pełna lista jest na /poradniki
const FEATURED_GUIDE_PATHS = ['/jak-zrobic-tabliczke-nagrobna', '/co-to-nekrolog', '/marketing-zakladu-pogrzebowego'];
const FEATURED_GUIDES = FEATURED_GUIDE_PATHS
  .map((path) => GUIDES.find((guide) => guide.to === path))
  .filter(Boolean);

const MATERIALS = [
  {
    name: "Nekrolog",
    icon: (
      <>
        <rect x="18" y="3" width="24" height="34" rx="1.5" fill="none" className="home_iconFrame" strokeWidth="2" />
        <path d="M30 9v8M26 12h8" className="home_iconInk" strokeWidth="1.8" />
        <path d="M22 23h16M22 27h16M24 31h12" className="home_iconInk" strokeWidth="1.6" />
      </>
    ),
  },
  {
    name: "Klepsydra",
    icon: (
      <>
        <rect x="10" y="6" width="40" height="28" rx="1.5" fill="none" className="home_iconFrame" strokeWidth="2" />
        <path d="M17 12v6M14.5 14h5" className="home_iconInk" strokeWidth="1.6" />
        <path d="M24 13h20M24 18h20M16 24h28M20 29h20" className="home_iconInk" strokeWidth="1.6" />
      </>
    ),
  },
  {
    name: "Tabliczka na krzyż",
    icon: (
      <>
        <rect x="4" y="9" width="52" height="22" rx="3" fill="none" className="home_iconFrame" strokeWidth="2" />
        <path d="M12 17h36M16 23h28" className="home_iconInk" strokeWidth="1.6" />
      </>
    ),
  },
  {
    name: "Tabliczka na znicz",
    icon: (
      <>
        <rect x="17" y="6" width="26" height="28" rx="2.5" fill="none" className="home_iconFrame" strokeWidth="2" />
        <path d="M23 15h14M23 20h14M25 25h10" className="home_iconInk" strokeWidth="1.6" />
      </>
    ),
  },
  {
    name: "Tabliczka na urnę",
    icon: (
      <>
        <ellipse cx="30" cy="20" rx="20" ry="14" fill="none" className="home_iconFrame" strokeWidth="2" />
        <path d="M20 17h20M23 23h14" className="home_iconInk" strokeWidth="1.6" />
      </>
    ),
  },
  {
    name: "Szarfa",
    icon: (
      <path
        d="M8 6h14l-2 28-5-4-5 4z M38 6h14l-2 28-5-4-5 4z"
        fill="none"
        className="home_iconFrame"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    ),
  },
];

const BENEFITS = [
  {
    title: "Spokojniejsza obsługa rodzin",
    text: "W trudnym momencie liczy się pewność i tempo. Nekrodruk porządkuje dane i skraca drogę od rozmowy do gotowego wydruku.",
  },
  {
    title: "Jednolity standard materiałów",
    text: "Powtarzalny wygląd nekrologów, klepsydr i tabliczek pomaga utrzymać profesjonalny wizerunek zakładu.",
  },
  {
    title: "Mniej ręcznej pracy",
    text: "Nie składasz wszystkiego od zera przy każdym zleceniu. Zapisujesz słowniki i szablony, a kolejne materiały powstają szybciej.",
  },
];

const OFFER_POINTS = [
  {
    title: "Dobór sprzętu",
    text: "Doradzimy, jaka drukarka i prasa termiczna sprawdzą się przy skali pracy Twojego zakładu.",
  },
  {
    title: "Konfiguracja stanowiska",
    text: "Pomożemy ustawić urządzenia i proces tak, żeby pierwsze tabliczki powstały bez przestojów.",
  },
  {
    title: "Wsparcie po wdrożeniu",
    text: "Zostajemy z Tobą także po starcie – pomagamy rozwiązać problemy i dopracować sposób pracy.",
  },
];

const FAQ_ITEMS = [
  {
    question: "Czy Nekrodruk jest przeznaczony tylko dla zakładów pogrzebowych?",
    answer:
      "To rozwiązanie projektowane przede wszystkim dla zakładów pogrzebowych, ale sprawdzi się też tam, gdzie przygotowuje się materiały funeralne do druku lub publikacji.",
  },
  {
    question: "Jakie materiały można przygotować w aplikacji?",
    answer:
      "Nekrologi, klepsydry, szarfy oraz tabliczki na krzyż, znicz i urnę – wszystkie z jednego formularza, gotowe do druku.",
  },
  {
    question: "Czy trzeba instalować dodatkowe programy graficzne?",
    answer:
      "Nie. Aplikacja działa w przeglądarce, a personalizacja projektu odbywa się bez zewnętrznych narzędzi graficznych.",
  },
  {
    question: "Czy mogę przetestować system przed rejestracją?",
    answer:
      "Tak – demo działa bez logowania, a po założeniu konta możesz sprawdzić wszystkie funkcje (pliki mają znak wodny do czasu wykupienia subskrypcji).",
  },
];

export default function MainPage() {
  return (
    <>
      <Helmet>
        <title>Nekrodruk.pl - Generator nekrologów, tabliczek i szarf online</title>
        <meta
          name="description"
          content="Nekrodruk.pl to program dla zakładów pogrzebowych. Z jednego formularza przygotujesz nekrolog, klepsydrę, szarfę i tabliczki na krzyż, znicz i urnę – gotowe do druku i termotransferu."
        />
        <meta
          name="keywords"
          content="generator nekrologów, nekrologi online, klepsydry, tabliczki na krzyż, tabliczki termotransfer, szarfy pogrzebowe, program dla zakładu pogrzebowego"
        />
        <meta name="author" content="Nekrodruk.pl" />
        <meta property="og:title" content="Nekrodruk.pl - Generator nekrologów online" />
        <meta
          property="og:description"
          content="Nekrologi, klepsydry, szarfy i tabliczki z jednego formularza. Gotowe szablony, personalizacja i demo bez logowania."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nekrodruk.pl/" />
        <link rel="canonical" href="https://nekrodruk.pl/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Nekrodruk",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "Aplikacja internetowa do tworzenia nekrologów, klepsydr, szarf oraz tabliczek na krzyż, znicz i urnę.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "PLN",
              description: "Możliwość sprawdzenia wersji demo",
            },
          })}
        </script>
      </Helmet>

      <div className="home">
        {/* Nagłówek */}
        <section className="home_hero">
          <div className="home_inner home_heroGrid">
            <div className="home_heroCopy">
              <span className="home_kicker home_kicker--light">Program dla zakładów pogrzebowych</span>
              <h1 className="home_heroTitle">
                Nekrologi, klepsydry, szarfy i tabliczki
                <em> z jednego formularza</em>
              </h1>
              <p className="home_heroLead">
                Wpisujesz dane zmarłego raz – Nekrodruk przygotowuje gotowe do druku pliki w jednolitym standardzie
                Twojego zakładu. Bez programów graficznych i bez przepisywania.
              </p>
              <div className="home_heroActions">
                <RouterLink to="/register" className="home_btn home_btn--light">
                  Załóż konto
                </RouterLink>
                <RouterLink to="/demo" className="home_btn home_btn--ghostLight">
                  Wypróbuj demo bez logowania
                </RouterLink>
              </div>
              <ul className="home_heroMeta">
                <li>Bez instalacji</li>
                <li>Praca w przeglądarce</li>
                <li>Wsparcie wdrożenia</li>
              </ul>
            </div>

            <div className="home_heroVisual">
              <figure className="home_plate home_plate--main">
                <img src={plateShield} alt="Złota tabliczka w kształcie tarczy z napisem Nekrodruk.pl" />
              </figure>
              <figure className="home_plate home_plate--side">
                <img src={plateGold} alt="Złota prostokątna tabliczka z napisem Nekrodruk.pl" />
              </figure>
              <span className="home_plateCaption">Przykładowe tabliczki wykonane z plików Nekrodruku</span>
            </div>
          </div>
        </section>

        {/* Materiały */}
        <section className="home_section home_section--tight" aria-labelledby="home-materials">
          <div className="home_inner">
            <div className="home_materialsCard">
              <h2 id="home-materials" className="home_title home_title--center">
                Jeden formularz – komplet materiałów
              </h2>
              <ul className="home_materials">
                {MATERIALS.map((item) => (
                  <li key={item.name} className="home_material">
                    <svg viewBox="0 0 60 40" aria-hidden="true">
                      {item.icon}
                    </svg>
                    <span>{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Czym jest */}
        <section className="home_section" id="WhatIs">
          <div className="home_inner">
            <div className="home_sectionHeader">
              <span className="home_kicker">O aplikacji</span>
              <h2 className="home_title">Czym jest Nekrodruk?</h2>
              <p className="home_lead">
                To aplikacja internetowa, w której przygotujesz <strong>nekrologi, klepsydry, szarfy i tabliczki</strong>{" "}
                bez instalowania programów. Do tego <strong>generator XML</strong>, eksport gotowych plików i własne
                szablony zakładu – zespół pracuje szybciej, a materiały zachowują jeden profesjonalny standard.
              </p>
            </div>
            <div className="home_cards home_cards--3">
              {BENEFITS.map((item) => (
                <article key={item.title} className="home_card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Jak to działa */}
        <section className="home_section home_section--soft" id="HowToUse">
          <div className="home_inner">
            <div className="home_sectionHeader">
              <span className="home_kicker">Jak to działa</span>
              <h2 className="home_title">Od formularza do gotowego wydruku</h2>
            </div>
            <HowItWorks />
          </div>
        </section>

        {/* Oferta: termotransfer i sprzęt */}
        <section className="home_section" id="Offer">
          <div className="home_inner">
            <div className="home_offer">
              <div className="home_offerVisual">
                <img src={heatPress} alt="Prasa termiczna do tabliczek" loading="lazy" />
              </div>
              <div className="home_offerCopy">
                <span className="home_kicker">Oferta</span>
                <h2 className="home_title">
                  Tabliczki metodą termotransferu
                  <em> prosto, we własnym zakładzie</em>
                </h2>
                <p className="home_lead">
                  Drukujesz projekt z Nekrodruku, umieszczasz go na metalowej tabliczce, stabilizujesz specjalną taśmą
                  i przenosisz nadruk prasą termiczną. Pomagamy przejść przez to od pierwszej rozmowy.
                </p>
                <ul className="home_offerPoints">
                  {OFFER_POINTS.map((point) => (
                    <li key={point.title}>
                      <strong>{point.title}</strong>
                      <span>{point.text}</span>
                    </li>
                  ))}
                </ul>
                <HashLink smooth to="/#Contact" className="home_btn home_btn--primary">
                  Zapytaj o wdrożenie
                </HashLink>
              </div>
            </div>

            <div className="home_custom">
              <div className="home_customVisual">
                <img src={plateGold} alt="Złota tabliczka z ozdobną ramką i napisem" loading="lazy" />
              </div>
              <div className="home_customCopy">
                <h3 className="home_subtitle">Projekty dopasowane do Twojego zakładu</h3>
                <div className="home_cards">
                  <article className="home_card home_card--accent">
                    <h3>Ramka z nazwą zakładu</h3>
                    <p>Nekrologi i klepsydry z Twoją ramką i nazwą zakładu – rozpoznawalne od pierwszego spojrzenia.</p>
                  </article>
                  <article className="home_card home_card--accent">
                    <h3>Kształty i ozdoby na wymiar</h3>
                    <p>Szablony w kształtach tabliczek, których używasz, z ornamentami takimi jak róża czy krzyż.</p>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Poradniki */}
        <section className="home_section home_section--soft" id="Poradniki">
          <div className="home_inner">
            <div className="home_sectionHeader">
              <span className="home_kicker">Poradniki</span>
              <h2 className="home_title">Pomagamy przygotować poprawne materiały</h2>
              <p className="home_lead">
                Praktyczne wskazówki dla rodzin i zakładów pogrzebowych – od treści nekrologu, przez tabliczki, po marketing zakładu.
              </p>
            </div>
            <div className="home_guides">
              {FEATURED_GUIDES.map((guide) => (
                <RouterLink key={guide.to} to={guide.to} className="home_guideCard">
                  <span className="home_kicker">Poradnik</span>
                  <strong>{guide.title}</strong>
                  <span className="home_guideText">{guide.text}</span>
                  <span className="home_guideMore">Czytaj poradnik →</span>
                </RouterLink>
              ))}
            </div>
            <div className="home_guidesFooter">
              <RouterLink to="/poradniki" className="home_btn home_btn--outline">
                Zobacz wszystkie poradniki
              </RouterLink>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="home_section">
          <div className="home_inner">
            <div className="home_sectionHeader">
              <span className="home_kicker">FAQ</span>
              <h2 className="home_title">Najczęściej zadawane pytania</h2>
            </div>
            <div className="home_cards home_cards--2">
              {FAQ_ITEMS.map((item) => (
                <article key={item.question} className="home_card">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Zachęta */}
        <section className="home_cta">
          <div className="home_inner home_ctaInner">
            <div>
              <h2 className="home_ctaTitle">
                Wypróbuj za darmo<em> – bez instalacji</em>
              </h2>
              <p>Załóż konto i sprawdź wszystkie funkcje. Subskrypcja od 49 zł miesięcznie.</p>
            </div>
            <div className="home_heroActions">
              <RouterLink to="/register" className="home_btn home_btn--light">
                Załóż konto
              </RouterLink>
              <RouterLink to="/demo" className="home_btn home_btn--ghostLight">
                Zobacz demo
              </RouterLink>
            </div>
          </div>
        </section>

        <div className="home_contact">
          <Contact />
        </div>
      </div>
    </>
  );
}
